import { useAuthStore } from "@/stores/auth";
import type { PageResponse } from "./types";

interface ApiOptions extends Omit<RequestInit, "body"> {
  body?: BodyInit | null | object;
  auth?: boolean;
}

export async function apiFetch<T = unknown>(
  path: string,
  options: ApiOptions = {}
): Promise<T> {
  const { auth = true, body, headers: extraHeaders, ...rest } = options;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(extraHeaders as Record<string, string> | undefined),
  };

  if (auth) {
    const token = useAuthStore.getState().session?.access_token;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const serializedBody =
    body && typeof body === "object" && !(body instanceof FormData) && !(body instanceof URLSearchParams)
      ? JSON.stringify(body)
      : (body as BodyInit | null | undefined);

  let response: Response;
  try {
    response = await fetch(path, {
      method: rest.method || "GET",
      headers,
      body: serializedBody,
      ...rest,
    });
  } catch (err) {
    // fetch only rejects on network failures (DNS, refused, CORS hard-fail).
    // Surface a Chinese message instead of the raw "Failed to fetch".
    throw new Error(
      err instanceof Error
        ? `网络错误：${err.message}（请检查 API 服务是否启动）`
        : `网络错误：${path}`,
    );
  }

  // 2026-05-20: guard against empty body so users don't see the cryptic
  // "Failed to execute 'json' on 'Response': Unexpected end of JSON input"
  // when the API process is down. We pre-read as text and parse manually.
  const text = await response.text();
  if (!text) {
    throw new Error(
      `服务端无响应（${response.status}）：${path}。可能是 API 服务未启动或代理失败。`,
    );
  }
  let payload: { code?: number; message?: string; data?: unknown };
  try {
    payload = JSON.parse(text);
  } catch {
    throw new Error(
      `服务端返回非 JSON（${response.status}）：${text.slice(0, 120)}…`,
    );
  }
  if (auth && response.status === 401) {
    useAuthStore.getState().clear();
  }

  if (!response.ok || payload.code !== 200) {
    throw new Error(payload.message || `请求失败：${path}`);
  }
  return payload.data as T;
}

// Paged fetch with structural validation. Throws if the response is not a
// proper { items: T[] } envelope so react-query enters its error state
// instead of handing the consumer a bogus object that crashes on `.filter`.
// Catches the 2026-05-18 class of bug at the boundary.
export async function apiFetchPaged<T>(
  path: string,
  options: ApiOptions = {},
): Promise<PageResponse<T>> {
  const data = await apiFetch<unknown>(path, options);
  if (!data || typeof data !== "object" || !Array.isArray((data as { items?: unknown }).items)) {
    throw new Error(
      `${path} 返回的不是分页结构 (期望 { items: [...] })`,
    );
  }
  // We trust the items array's element shape — TypeScript declares it; runtime
  // can't easily enforce it without a schema lib. The structural items-array
  // check is what prevents the class of "object passed where array expected"
  // bugs that crashed the UI before.
  return data as PageResponse<T>;
}

export type Session = {
  access_token: string;
  refresh_token: string;
};

export type User = {
  user_id: number;
  username: string;
  phone: string;
  email?: string | null;
  avatar_url?: string | null;
  auth_provider?: string;
  role: string;
  credit_balance: number;
};

export type AuthResult = {
  access_token: string;
  refresh_token: string;
  user: User;
};
